import { Chess, DEFAULT_POSITION, type PieceSymbol } from 'chess.js';
import { CHESS_TICK_MS } from './constants';
import type { ChessMove, Inputs, PlayerId, PromotionPiece, Square, State } from './types';

export type { PieceSymbol } from 'chess.js';

// The board: one game of chess across every chess phase, living in step() like every other rule
// (rule 4). chess.js is the rule book; it is loaded from the FEN only when it is needed (a move, the
// legal moves for the client), so a tick without a move costs nothing but a subtraction. chess.js
// is pure (no clock, no Math.random, no DOM; checked 2026-10-24), so rule 1 holds.

export type ChessColor = 'w' | 'b';

/** The starting position. */
export const START_FEN: string = DEFAULT_POSITION;

export function chessColorOf(s: State, id: PlayerId): ChessColor {
  return s.chess.white === id ? 'w' : 'b';
}

/** Whose turn it is, read straight off the FEN. */
export function colorToMove(s: State): ChessColor {
  return s.chess.fen.split(' ')[1] === 'b' ? 'b' : 'w';
}

/** The player whose turn it is. */
export function toMove(s: State): PlayerId {
  return colorToMove(s) === 'w' ? s.chess.white : s.chess.white === 0 ? 1 : 0;
}

function load(s: State): Chess {
  return new Chess(s.chess.fen);
}

function toChessMove(m: { from: Square; to: Square; promotion?: PieceSymbol }): ChessMove {
  return m.promotion ? { from: m.from, to: m.to, promotion: m.promotion as PromotionPiece } : { from: m.from, to: m.to };
}

/** Every legal move in the position, or those from one square; for the client's target dots. */
export function legalChessMoves(s: State, from?: Square): ChessMove[] {
  const game = load(s);
  const moves = from === undefined ? game.moves({ verbose: true }) : game.moves({ verbose: true, square: from });
  return moves.map(toChessMove);
}

/** Geometric premove targets; occupancy and king safety are checked only when the turn arrives. */
export function premoveChessMoves(s: State, id: PlayerId, from: Square): ChessMove[] {
  const game = load(s);
  const piece = game.get(from);
  if (!piece || piece.color !== chessColorOf(s, id)) return [];
  const file = from.charCodeAt(0) - 97;
  const rank = Number(from[1]);
  const moves: ChessMove[] = [];
  for (let f = 0; f < 8; f++) for (let r = 1; r <= 8; r++) {
    const dx = Math.abs(f - file);
    const dy = Math.abs(r - rank);
    if (dx + dy === 0) continue;
    let possible = false;
    switch (piece.type) {
      case 'n': possible = dx * dy === 2; break;
      case 'b': possible = dx === dy; break;
      case 'r': possible = dx === 0 || dy === 0; break;
      case 'q': possible = dx === dy || dx === 0 || dy === 0; break;
      case 'k': possible = Math.max(dx, dy) === 1 || (file === 4 && rank === (piece.color === 'w' ? 1 : 8) && dy === 0 && dx === 2); break;
      case 'p': {
        const forward = (r - rank) * (piece.color === 'w' ? 1 : -1);
        possible = (forward === 1 && dx <= 1) || (forward === 2 && dx === 0 && rank === (piece.color === 'w' ? 2 : 7));
        break;
      }
    }
    if (!possible) continue;
    const to = `${String.fromCharCode(97 + f)}${r}` as Square;
    if (piece.type === 'p' && (r === 1 || r === 8)) {
      for (const promotion of ['q', 'r', 'b', 'n'] as const) moves.push({ from, to, promotion });
    } else moves.push({ from, to });
  }
  return moves;
}

/** Is the player to move in check? */
export function inCheck(s: State): boolean {
  return load(s).inCheck();
}

export type BoardSquare = { square: Square; type: PieceSymbol; color: ChessColor } | null;

/** The 8x8 board, rank 8 first (as chess.js returns it); for the renderer. */
export function boardOf(s: State): BoardSquare[][] {
  return load(s).board();
}

/**
 * Plays `move` for `id` if it is their turn and the move is legal; returns whether it was. A legal
 * move updates the position and may end the match: mate wins for the mover, stalemate and
 * insufficient material are a draw. Repetition and the fifty-move rule do not count (they would
 * need the move history in the state; repeating costs clock anyway).
 */
export function applyChessMove(s: State, id: PlayerId, move: ChessMove): boolean {
  if (s.winner !== null || toMove(s) !== id) return false;
  const game = load(s);
  let made;
  try {
    made = game.move({ from: move.from, to: move.to, promotion: move.promotion });
  } catch {
    return false;
  }
  s.chess.fen = game.fen();
  s.chess.lastMove = toChessMove(made);
  s.chess.lastMoveTick = s.tick;
  if (game.isCheckmate()) {
    s.winner = id;
    s.outcome = 'checkmate';
  } else if (game.isStalemate()) {
    s.winner = 'draw';
    s.outcome = 'stalemate';
  } else if (game.isInsufficientMaterial()) {
    s.winner = 'draw';
    s.outcome = 'material';
  }
  return true;
}

/**
 * One tick at the board: the player to move may move (at most one move per tick: only the input of
 * the player to move is looked at), then the clock of whoever is to move afterwards loses a tick.
 * The clock stops at 0; stepMatchEnd turns that into the result.
 */
export function stepChess(s: State, inputs: Inputs): void {
  const mover = toMove(s);
  const move = inputs[mover].chessMove;
  if (move) applyChessMove(s, mover, move);
  if (s.winner !== null) return;
  const thinking = s.players[toMove(s)];
  thinking.chessClockMs = Math.max(0, thinking.chessClockMs - CHESS_TICK_MS);
}

// --- search support for the bot -----------------------------------------------------------------------
// chess.js's public moves({ verbose: true }) and move() build a SAN string and two FENs for every move,
// each with a legal-move generation of its own: about 1.5 ms per node in a middlegame (measured
// 2026-10-24), while the generator itself takes a microsecond. A search cannot afford that, so the bot's
// position reaches for the private generator and make/undo the way chess.js's own Move class does
// (chess['_moves']). Pinned to chess.js 1.4; openSearch() throws and chessBot.test.ts fails loudly should
// an update rename them. Everything chess.js stays in this file (rule 8).

interface InternalMove {
  color: ChessColor;
  /** 0x88 indices */
  from: number;
  to: number;
  piece: PieceSymbol;
  captured?: PieceSymbol;
  promotion?: PieceSymbol;
  flags: number;
}

interface ChessInternals {
  _moves(options: { legal: boolean }): InternalMove[];
  _makeMove(move: InternalMove): void;
  _undoMove(): InternalMove | null;
  _isKingAttacked(color: ChessColor): boolean;
  _board: ({ type: PieceSymbol; color: ChessColor } | undefined)[];
}

/** A move as the search sees it: the ChessMove plus what move ordering needs. */
export interface SearchMove extends ChessMove {
  piece: PieceSymbol;
  captured?: PieceSymbol;
  /** chess.js's internal move, handed back to tryMake(); opaque to everyone else */
  raw: object;
}

export interface PieceOnBoard {
  type: PieceSymbol;
  color: ChessColor;
  /** 0 = a … 7 = h */
  file: number;
  /** 0 = rank 1 … 7 = rank 8 */
  rank: number;
}

/**
 * A position opened for a search: transient, never part of the State. Moves come pseudo-legal (the
 * own king may be left in check) and are checked when they are made, because legality costs a make/undo
 * per move and a search makes only a few of the moves it generates.
 */
export interface SearchPosition {
  turn(): ChessColor;
  inCheck(): boolean;
  insufficientMaterial(): boolean;
  /** pseudo-legal moves of the side to move, in chess.js's generation order (castling is already legal) */
  moves(): SearchMove[];
  /** makes the move if it leaves the own king safe and says so; otherwise the position is unchanged */
  tryMake(move: SearchMove): boolean;
  undo(): void;
  /** every piece on the board, for the evaluation */
  pieces(): PieceOnBoard[];
}

const FILES = 'abcdefgh';

function squareName(sq: number): Square {
  return `${FILES[sq & 7]}${8 - (sq >> 4)}` as Square;
}

function toSearchMove(m: InternalMove): SearchMove {
  const move: SearchMove = { from: squareName(m.from), to: squareName(m.to), piece: m.piece, raw: m };
  if (m.promotion) move.promotion = m.promotion as PromotionPiece;
  if (m.captured) move.captured = m.captured;
  return move;
}

export function openSearch(fen: string): SearchPosition {
  const game = new Chess(fen);
  const g = game as unknown as Partial<ChessInternals>;
  if (typeof g._moves !== 'function' || typeof g._makeMove !== 'function' || typeof g._undoMove !== 'function' || typeof g._isKingAttacked !== 'function' || !Array.isArray(g._board)) {
    throw new Error('chess.js internals changed (_moves/_makeMove/_undoMove/_isKingAttacked/_board): revisit openSearch');
  }
  const internals = g as ChessInternals;
  return {
    turn: () => game.turn(),
    inCheck: () => game.inCheck(),
    insufficientMaterial: () => game.isInsufficientMaterial(),
    moves: () => internals._moves({ legal: false }).map(toSearchMove),
    tryMake(move) {
      const us = game.turn();
      internals._makeMove(move.raw as InternalMove);
      if (internals._isKingAttacked(us)) {
        internals._undoMove();
        return false;
      }
      return true;
    },
    undo() {
      internals._undoMove();
    },
    pieces() {
      const out: PieceOnBoard[] = [];
      const board = internals._board;
      for (let sq = 0; sq <= 0x77; sq++) {
        if (sq & 0x88) {
          sq += 7;
          continue;
        }
        const piece = board[sq];
        if (piece) out.push({ type: piece.type, color: piece.color, file: sq & 7, rank: 7 - (sq >> 4) });
      }
      return out;
    },
  };
}
